/**
 * Decorative soil-intelligence atmosphere for shell and authentication use.
 *
 * The backdrop has no application meaning, accepts no interaction, and uses
 * one finite reveal rather than a continuously running animation.
 */
import { keyframes } from "@emotion/react";
import { Box, useMediaQuery } from "@mui/material";

import { reducedMotionMediaQuery } from "../../theme/motionTokens";

const ambientReveal = keyframes`
	from { opacity: 0; transform: scale(1.015); }
	to { opacity: 1; transform: scale(1); }
`;

export function AmbientBackdrop() {
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);

	return (
		<Box
			aria-hidden="true"
			data-flaha-ambient="soil-intelligence"
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			sx={(theme) => ({
				position: "fixed",
				inset: 0,
				zIndex: 0,
				overflow: "hidden",
				pointerEvents: "none",
				userSelect: "none",
				backgroundColor: theme.palette.workspace.background,
				backgroundImage: `
					radial-gradient(circle at 14% 18%, rgba(119, 169, 87, 0.16), transparent 28%),
					radial-gradient(circle at 84% 12%, rgba(74, 156, 197, 0.14), transparent 30%),
					radial-gradient(circle at 76% 82%, rgba(169, 119, 66, 0.17), transparent 34%),
					linear-gradient(145deg, ${theme.palette.workspace.background} 0%, #15110F 52%, #0D1210 100%)
				`,
				"&::before": {
					content: '""',
					position: "absolute",
					inset: "-5%",
					opacity: 0.34,
					backgroundImage: `
						linear-gradient(rgba(246, 240, 227, 0.035) 1px, transparent 1px),
						linear-gradient(90deg, rgba(246, 240, 227, 0.035) 1px, transparent 1px)
					`,
					backgroundSize: "48px 48px",
					maskImage:
						"linear-gradient(to bottom, transparent, black 24%, black 78%, transparent)",
					animation: `${ambientReveal} ${theme.motion.duration.entry + 300}ms ${theme.motion.easing.enter} both`,
				},
				"&::after": {
					content: '""',
					position: "absolute",
					inset: 0,
					opacity: 0.46,
					backgroundImage: `
						radial-gradient(circle, rgba(246, 240, 227, 0.28) 0 0.75px, transparent 1px),
						radial-gradient(circle, rgba(119, 169, 87, 0.2) 0 0.7px, transparent 1px),
						linear-gradient(118deg, transparent 46%, rgba(74, 156, 197, 0.07) 49%, transparent 52%)
					`,
					backgroundPosition: "0 0, 18px 22px, center",
					backgroundSize: "36px 36px, 54px 54px, 100% 100%",
					maskImage:
						"radial-gradient(ellipse at center, black 0%, transparent 72%)",
					animation: `${ambientReveal} ${theme.motion.duration.entry + 500}ms ${theme.motion.easing.enter} both`,
				},
				[reducedMotionMediaQuery]: {
					"&::before, &::after": {
						animation: "none",
						transform: "none",
					},
				},
			})}
		/>
	);
}
